import React from "react";
import {useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent, Stack} from "@mui/material";
import {
  BookmarkBorderOutlined,
  DirectionsOutlined,
  ExpandMore,
  ExploreOutlined,
  MapOutlined,
  SearchOutlined,
  ShareOutlined,
} from "@mui/icons-material";
import {AppNavigator} from "../../components/AppNavigator";
import {PageContainer} from "../../components/layout/PageContainer";
import icon from "../../assets/icon.png";
import "./HomePage.css";

type RegionGroup = {group: string; items: string[]};

const FEATURES: {icon: React.ReactNode; title: string; text: string}[] = [
  {icon: <ExploreOutlined/>, title: "Explore", text: "Discover dog-friendly parks by map or search, with amenities and ratings for each."},
  {icon: <ShareOutlined/>, title: "Share", text: "Send any park to a friend with the built-in share button."},
  {icon: <DirectionsOutlined/>, title: "Navigate", text: "Get there with directions via Google or Apple Maps."},
  {icon: <BookmarkBorderOutlined/>, title: "Personalise", text: "Save your favourite parks into custom groups."},
];

const COMING_SOON: {title: string; text: string}[] = [
  {title: "Socialize", text: "Connect with the dog-lover community, share tips, and arrange play dates."},
  {title: "Stay informed", text: "Get community updates on park conditions and events."},
  {title: "Personalise +", text: "Set reminders and track your visits."},
];

const AVAILABLE_REGIONS: RegionGroup[] = [
  {group: "Upper North Island", items: ["Far North District", "Whangārei District", "Kaipara District", "Auckland (points only, not park outlines)"]},
  {group: "Central North Island", items: ["Hamilton City", "Waikato District", "Waipā District", "Tauranga City", "Taupō District", "Gisborne District", "Ruapehu District", "New Plymouth District"]},
  {group: "Lower North Island", items: ["Hastings District", "Palmerston North City", "Manawatū District", "Rangitīkei District", "Wellington City", "Lower Hutt City", "Upper Hutt City", "Porirua City", "Kāpiti Coast District", "Greater Wellington Region"]},
  {group: "South Island", items: ["Nelson City", "Tasman District", "Marlborough District", "Kaikōura District", "Hurunui District", "Waimakariri District", "Christchurch City", "Ashburton District", "Dunedin"]},
];

const HELP_REGIONS: RegionGroup[] = [
  {group: "Upper North Island", items: ["Auckland (all park outlines)"]},
  {group: "Central North Island", items: ["Rotorua", "Bay of Plenty"]},
  {group: "Lower North Island", items: ["Wairarapa"]},
  {group: "South Island", items: ["Invercargill"]},
];

function RegionAccordions({regions}: {regions: RegionGroup[]}) {
  return (
    <Stack spacing={1}>
      {regions.map((region) => (
        <Accordion key={region.group} disableGutters>
          <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography variant="subtitle1">{region.group}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack component="ul" spacing={0.5} sx={{m: 0, pl: 2.5}}>
              {region.items.map((item) => (
                <Typography key={item} component="li" variant="body2" color="text.secondary">{item}</Typography>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

export function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="HomePage">
      <div className="HomePageContent">
        <PageContainer sx={{display: "flex", flexDirection: "column", gap: 5}}>

          {/* Hero */}
          <Box sx={{mt: 1, textAlign: "center", bgcolor: "primary.light", borderRadius: 1.5, px: 3, py: 5}}>
            <Box component="img" src={icon} alt="Dog Zones logo" sx={{width: 72, height: 72, borderRadius: 1.5, mb: 2}}/>
            <Typography variant="h3" component="h1" sx={{fontWeight: 700}}>Dog Zones</Typography>
            <Typography variant="body1" color="text.secondary" sx={{mt: 1, mx: "auto", maxWidth: 440}}>
              Your guide to dog-friendly parks across New Zealand. Find a good spot for your pup to play, socialise
              and run around. 🐾
            </Typography>
            <Stack direction={{xs: "column", sm: "row"}} spacing={1.5} justifyContent="center" sx={{mt: 3}}>
              <Button variant="contained" size="large" startIcon={<MapOutlined/>} onClick={() => navigate("/map")}>
                Explore the map
              </Button>
              <Button variant="outlined" size="large" startIcon={<SearchOutlined/>} onClick={() => navigate("/search")}>
                Search parks
              </Button>
            </Stack>
          </Box>

          {/* Features */}
          <Box>
            <Typography variant="h5" sx={{mb: 2}}>What you can do</Typography>
            <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
              {FEATURES.map((f) => (
                <Card key={f.title} variant="outlined">
                  <CardContent>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 1, mb: 1.5,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: "primary.light", color: "primary.main",
                    }}>{f.icon}</Box>
                    <Typography variant="subtitle1">{f.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{f.text}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>

          {/* Available regions */}
          <Box>
            <Typography variant="h5" sx={{mb: 0.5}}>Available regions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
              Councils and regions currently covered.
            </Typography>
            <RegionAccordions regions={AVAILABLE_REGIONS}/>
          </Box>

          {/* Coming soon */}
          <Box>
            <Typography variant="h5" sx={{mb: 2}}>Coming soon</Typography>
            <Stack spacing={1.5}>
              {COMING_SOON.map((c) => (
                <Box key={c.title}>
                  <Typography variant="subtitle1">{c.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{c.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Help us grow */}
          <Box>
            <Typography variant="h5" sx={{mb: 0.5}}>Help us grow</Typography>
            <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
              Dog Zones' mission is to document every dog park in New Zealand. I'm building it solo, so any help is
              welcome. If you know a park that isn't on the list, get in touch. These are the areas we're hoping to
              cover next:
            </Typography>
            <RegionAccordions regions={HELP_REGIONS}/>
          </Box>

          {/* Feedback */}
          <Box sx={{textAlign: "center", bgcolor: "primary.light", borderRadius: 1.5, px: 3, py: 4}}>
            <Typography variant="h6">📣 We value your bark!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{mt: 1, mb: 3, mx: "auto", maxWidth: 460}}>
              Your feedback is the treat that helps Dog Zones improve. Got a new park to add or an idea to share?
              Let's make Dog Zones better together. 🐾💬
            </Typography>
            <Button href="mailto:contact@dog-zones.com" variant="contained" size="large">Contact us</Button>
          </Box>

        </PageContainer>
      </div>
      <AppNavigator/>
    </div>
  );
}
